import { haversineKm, bearingBetween } from './geomath';

/**
 * Curviness ("twistiness") of a route — the motorcycle metric.
 *
 * Walks the route geometry, sums the absolute heading change at each vertex,
 * and divides by total distance to get degrees-turned-per-km. Pure geometry,
 * no API calls.
 *
 * @param {{coordinates: number[][]}} geometry  GeoJSON LineString ([lng,lat] pairs)
 * @returns {{ degPerKm:number, level:number, label:string, chilis:number } | null}
 */
export function computeCurviness(geometry) {
  const coords = geometry?.coordinates;
  if (!coords || coords.length < 3) return null;

  let totalTurn = 0; // degrees
  let totalKm   = 0;
  let prevBearing = null;

  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1];
    const [lng2, lat2] = coords[i];
    const segKm = haversineKm(lat1, lng1, lat2, lng2);
    if (segKm < 0.0005) continue; // skip near-duplicate points (noise)

    const bearing = bearingBetween(lat1, lng1, lat2, lng2);
    if (prevBearing !== null) {
      let diff = Math.abs(bearing - prevBearing) % 360;
      if (diff > 180) diff = 360 - diff;
      totalTurn += diff;
    }
    prevBearing = bearing;
    totalKm += segKm;
  }

  if (totalKm < 0.1) return null;
  const degPerKm = totalTurn / totalKm;
  return { degPerKm, ...classify(degPerKm) };
}

// Thresholds tuned for road riding (deg of heading change per km).
function classify(degPerKm) {
  if (degPerKm < 40)  return { level: 0, label: 'Straight',     chilis: 0 };
  if (degPerKm < 100) return { level: 1, label: 'Flowing',      chilis: 1 };
  if (degPerKm < 200) return { level: 2, label: 'Twisty',       chilis: 2 };
  return                     { level: 3, label: 'Very Twisty',  chilis: 3 };
}

/**
 * Share of a route that is curvy vs straight, by distance.
 * Classifies each segment by its local heading-change rate; segments turning
 * faster than ~30°/km count as "curves". Returns null for trivial geometry.
 */
export function computeRoadBreakdown(geometry) {
  const coords = geometry?.coordinates;
  if (!coords || coords.length < 3) return null;

  const bearings = [];
  for (let i = 1; i < coords.length; i++) {
    const [lo1, la1] = coords[i - 1], [lo2, la2] = coords[i];
    bearings.push(bearingBetween(la1, lo1, la2, lo2));
  }

  let curvyKm = 0, straightKm = 0;
  for (let i = 0; i < bearings.length; i++) {
    const [lo1, la1] = coords[i], [lo2, la2] = coords[i + 1];
    const segKm = haversineKm(la1, lo1, la2, lo2);
    if (segKm < 0.0005) continue;
    let turn = 0;
    if (i > 0) { let d = Math.abs(bearings[i] - bearings[i - 1]) % 360; if (d > 180) d = 360 - d; turn = d; }
    if (turn / segKm > 30) curvyKm += segKm; else straightKm += segKm;
  }
  const total = curvyKm + straightKm;
  if (total < 0.1) return null;
  return {
    curvyPct: Math.round((curvyKm / total) * 100),
    straightPct: Math.round((straightKm / total) * 100),
  };
}

export const CURVINESS_COLORS = {
  0: '#6b7280', // straight — grey
  1: '#00c2a8', // flowing — teal
  2: '#ff7a1a', // twisty — orange
  3: '#f5564a', // very twisty — coral
};
